import { schema, supabase } from "./supabase";
import type { JsonRecord, PermissionRow, UserProfile, WorkContext } from "../types";

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  return data as T;
}

async function throwFunctionError(error: unknown): Promise<never> {
  if (error && typeof error === "object" && "context" in error) {
    const response = (error as { context?: unknown }).context;
    if (response instanceof Response) {
      const payload = await response.clone().json().catch(() => null) as { error?: string } | null;
      if (payload?.error) throw new Error(payload.error);
    }
  }
  throw error;
}

export async function bootstrapSession() {
  const obra = schema("obra");
  const [profileResult, permissionsResult] = await Promise.all([
    obra.rpc("get_my_profile" as never),
    obra.rpc("get_my_project_permissions" as never, {
      p_proyecto_id: null,
      p_contrato_id: null,
    } as never),
  ]);

  const profiles = unwrap(profileResult.data, profileResult.error) as unknown as UserProfile[];
  const permissions = unwrap(
    permissionsResult.data,
    permissionsResult.error,
  ) as unknown as PermissionRow[];

  return { profile: profiles?.[0] ?? null, permissions: permissions ?? [] };
}

export async function qualityApi(
  functionName:
    | "api_calidad_especificaciones"
    | "api_calidad_actividades_materiales"
    | "api_calidad_entregables",
  operation: string,
  context: WorkContext,
  payload: JsonRecord = {},
) {
  const { data, error } = await schema("calidad").rpc(functionName as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error) as unknown;
}

export async function resultApi(operation: string, context: WorkContext, payload: JsonRecord = {}) {
  const { data, error } = await schema("calidad").rpc("api_calidad_resultados" as never, {
    op: operation,
    pid: context.proyecto_id,
    cid: context.contrato_id,
    j: payload,
  } as never);
  return unwrap(data, error) as unknown;
}

export async function listQualityRows(table: string, context: WorkContext) {
  const { data, error } = await schema("calidad")
    .from(table)
    .select("*")
    .eq("proyecto_id", context.proyecto_id)
    .eq("contrato_id", context.contrato_id)
    .order("created_at", { ascending: false })
    .limit(250);
  return unwrap(data, error) as unknown as JsonRecord[];
}

export interface QualityResultFilters {
  start?: string;
  end?: string;
  frontId?: string;
  subfrontId?: string;
  dateColumn?: string;
  materialIds?: string[];
  excludeMaterialIds?: boolean;
  page?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

/** Project/contract-scoped, server-filtered access to an explicitly configured result table. */
export async function listQualityResults(table: string, context: WorkContext, filters: QualityResultFilters = {}) {
  const page = filters.page ?? 0;
  const pageSize = filters.pageSize ?? 100;
  let query = schema("calidad")
    .from(table)
    .select("*", { count: "exact" })
    .eq("proyecto_id", context.proyecto_id)
    .eq("contrato_id", context.contrato_id);
  if (filters.start && filters.dateColumn) query = query.gte(filters.dateColumn, filters.start);
  if (filters.end && filters.dateColumn) query = query.lte(filters.dateColumn, filters.end);
  if (filters.frontId) query = query.eq("frente_id", filters.frontId);
  if (filters.subfrontId) query = query.eq("subfrente_id", filters.subfrontId);
  if (filters.materialIds?.length) {
    query = filters.excludeMaterialIds
      ? query.not("actividad_material_id", "in", `(${filters.materialIds.join(",")})`)
      : query.in("actividad_material_id", filters.materialIds);
  }
  if (filters.signal) query = query.abortSignal(filters.signal);
  const { data, error, count } = await query.range(page * pageSize, (page + 1) * pageSize - 1);
  return { rows: unwrap(data, error) as unknown as JsonRecord[], count: count ?? 0 };
}

export type SamplingKind =
  | "steel"
  | "aggregates"
  | "compaction"
  | "concrete"
  | "bolts"
  | "ndt"
  | "masonry"
  | "coating";

export interface SamplingFilters {
  start?: string;
  end?: string;
  frontId?: string;
  subfrontIds?: string[];
  deliverableIds?: string[];
  status?: string;
}

export interface ConcreteCastFilters {
  date?: string;
  strength?: string;
  frontId?: string;
  subfrontId?: string;
}

export interface CompactionLayerFilters {
  date?: string;
  frontId?: string;
  subfrontId?: string;
  structureId?: string;
}

async function samplingApi(
  operation: string,
  context: WorkContext,
  payload: JsonRecord = {},
) {
  const { data, error } = await schema("calidad").rpc("api_muestreos_pruebas" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error) as unknown;
}

const SAMPLING_API_CATALOG_OPERATIONS = new Set<string>([
  "mezclas_cementantes_contrato",
  "especificaciones_material_contrato",
  "entregables_especificacion",
  "unidades_fresco",
]);

export async function samplingCatalogs(context: WorkContext) {
  return await samplingApi("catalogos", context) as JsonRecord;
}

export async function concreteSamplingCatalogs(context: WorkContext) {
  return await samplingApi("catalogos_concreto", context) as JsonRecord;
}

export async function listConcreteCasts(
  context: WorkContext,
  filters: ConcreteCastFilters = {},
) {
  return await samplingApi("colados_concreto", context, {
    fecha_inicio: filters.date || null,
    fecha_fin: filters.date || null,
    resistencia: filters.strength || null,
    frente_id: filters.frontId || null,
    subfrente_id: filters.subfrontId || null,
  }) as JsonRecord[];
}

async function compactionApi(
  operation: string,
  context: WorkContext,
  payload: JsonRecord = {},
) {
  const { data, error } = await schema("calidad").rpc("api_compactacion_suelos" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error) as unknown;
}

export async function compactionSamplingCatalogs(context: WorkContext) {
  return await compactionApi("catalogos", context) as JsonRecord;
}

export async function listCompactionLayers(
  context: WorkContext,
  filters: CompactionLayerFilters = {},
) {
  return await compactionApi("capas", context, {
    fecha: filters.date || null,
    frente_id: filters.frontId || null,
    subfrente_id: filters.subfrontId || null,
    estructura_id: filters.structureId || null,
  }) as JsonRecord[];
}

export async function createCompactionSampling(context: WorkContext, values: JsonRecord) {
  return await compactionApi("insertar", context, { datos: values });
}

export async function samplingFormCatalog(
  operation: string,
  context: WorkContext,
  payload: JsonRecord = {},
) {
  if (SAMPLING_API_CATALOG_OPERATIONS.has(operation)) {
    return await samplingApi(operation, context, payload) as JsonRecord[];
  }
  const { data, error } = await schema("calidad").rpc("api_catalogos_muestreos_pruebas" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error) as unknown as JsonRecord[];
}

export async function contractualSamplingCatalog(
  operation: string,
  context: WorkContext,
  payload: JsonRecord = {},
) {
  const { data, error } = await schema("calidad").rpc("api_catalogos_muestreos_pruebas" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error) as unknown as JsonRecord[];
}

export async function listSamplingRecords(context: WorkContext, filters: SamplingFilters = {}) {
  const data = await samplingApi("listar", context, {
    fecha_inicio: filters.start || null,
    fecha_fin: filters.end || null,
    frente_id: filters.frontId || null,
    subfrente_ids: filters.subfrontIds || [],
    entregable_ids: filters.deliverableIds || [],
  }) as JsonRecord[];
  return data.map((row) => ({
    ...row,
    record_type: "sampling",
  })) as JsonRecord[];
}

export async function createSamplingRecord(
  kind: Exclude<SamplingKind, "bolts">,
  context: WorkContext,
  values: JsonRecord,
) {
  return samplingApi("insertar", context, { tipo: kind, datos: values });
}

export async function updateSamplingRecord(context: WorkContext, id: string, values: JsonRecord) {
  return samplingApi("editar", context, { id, datos: values });
}

export async function deleteSamplingRecord(context: WorkContext, id: string) {
  return samplingApi("eliminar", context, { id });
}

export interface ReportReviewFilters {
  start?: string;
  end?: string;
  search?: string;
  frontId?: string;
  subfrontIds?: string[];
  studyIds?: string[];
  status?: string;
}

export interface ObservedReportFilters {
  start?: string;
  end?: string;
  status?: string;
  laboratoryId?: string;
  frontId?: string;
}

export interface PendingReportFilters {
  start?: string;
  end?: string;
  frontId?: string;
  studyIds?: string[];
}

export type UnitPendingReportType =
  | "steel"
  | "aggregates"
  | "ballast"
  | "qualities"
  | "masonry"
  | "pedraplen";

export type GroupablePendingReportType =
  | "ndt"
  | "connections"
  | "concrete"
  | "compaction";

export type PendingReportType =
  | UnitPendingReportType
  | GroupablePendingReportType;

/** Row returned by `pendientes_registro`; `detalles` is composed by the backend. */
export interface PendingReportRow extends JsonRecord {
  tipo: PendingReportType;
  detalles: string | null;
}

/** The only client entry point for reception/review operations. */
export async function reportReviewApi(
  operation: "listar" | "detalle" | "observados" | "catalogos" | "pendientes_registro" | "pendientes_agrupables" | "faltantes" | "registrar" | "registrar_agrupado" | "editar" | "eliminar",
  context: WorkContext,
  payload: JsonRecord = {},
) {
  const { data, error } = await schema("calidad").rpc("api_revision_muestreos_pruebas" as never, {
    p_operacion: operation,
    p_proyecto_id: context.proyecto_id,
    p_contrato_id: context.contrato_id,
    p_payload: payload,
  } as never);
  return unwrap(data, error) as unknown;
}

/** END, bolted-connection, concrete, and compaction samples not assigned to a report. */
export async function listGroupablePendingReports(context: WorkContext, filters: PendingReportFilters = {}) {
  return toRows(await reportReviewApi("pendientes_agrupables", context, {
    fecha_inicio: filters.start || null,
    fecha_fin: filters.end || null,
    frente_id: filters.frontId || null,
    entregable_ids: filters.studyIds || [],
  })).filter((row) => row.informe_id == null) as PendingReportRow[];
}

export async function listReceivedReports(context: WorkContext, filters: ReportReviewFilters = {}) {
  return toRows(await reportReviewApi("listar", context, {
    fecha_inicio: filters.start || null,
    fecha_fin: filters.end || null,
    busqueda: filters.search?.trim() || null,
    frente_id: filters.frontId || null,
    subfrente_ids: filters.subfrontIds || [],
    entregable_ids: filters.studyIds || [],
    estatus: filters.status || null,
  }));
}

export async function listObservedReports(context: WorkContext, filters: ObservedReportFilters = {}) {
  return toRows(await reportReviewApi("observados", context, {
    fecha_inicio: filters.start || null,
    fecha_fin: filters.end || null,
    estatus: filters.status || null,
    laboratorio_id: filters.laboratoryId || null,
    frente_id: filters.frontId || null,
  }));
}

export async function listPendingReports(context: WorkContext, filters: PendingReportFilters = {}) {
  return toRows(await reportReviewApi("pendientes_registro", context, {
    fecha_inicio: filters.start || null,
    fecha_fin: filters.end || null,
    frente_id: filters.frontId || null,
    entregable_ids: filters.studyIds || [],
  })) as PendingReportRow[];
}

export async function communicationCatalogs(context: WorkContext) {
  const { data, error } = await schema("control_documental").rpc(
    "api_catalogos_comunicacion_oficial" as never,
    { p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id } as never,
  );
  return unwrap(data, error) as unknown as JsonRecord;
}

export type CommunicationDeliverable = {
  id: string;
  nombre: string;
  origen: "Genérico" | "Contrato";
};

export async function listCommunicationDeliverables(context: WorkContext) {
  const { data, error } = await schema("control_documental").rpc(
    "api_entregables_comunicacion" as never,
    { p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id } as never,
  );
  return (toRows(unwrap(data, error) as unknown) as CommunicationDeliverable[])
    .sort((left, right) => left.nombre.localeCompare(right.nombre, "es"));
}

export async function searchCommunications(
  context: WorkContext,
  filters: {
    search?: string;
    start?: string;
    end?: string;
    typeIds?: string[];
    deliverableIds?: string[];
    subjectTypeIds?: string[];
    emisor?: string;
    frontId?: string;
    subfrontIds?: string[];
    status?: string;
  } = {},
) {
  const typeIds = filters.typeIds?.length ? filters.typeIds : [null];
  const subjectTypeIds = filters.subjectTypeIds?.length ? filters.subjectTypeIds : [null];
  const requests = typeIds.flatMap((typeId) => subjectTypeIds.map(async (subjectTypeId) => {
    const collected: JsonRecord[] = [];
    const limit = 500;
    for (let offset = 0; ; offset += limit) {
      const { data, error } = await schema("control_documental").rpc(
        "api_buscar_comunicaciones_oficiales" as never,
        {
        p_proyecto_id: context.proyecto_id,
        p_contrato_id: context.contrato_id,
        p_area_id: null,
        p_tipo_doc_id: typeId,
        p_actividad_material_ids: null,
        p_tipo_asunto_id: subjectTypeId,
        p_frente_id: filters.frontId || null,
        p_subfrente_ids: filters.subfrontIds?.length ? filters.subfrontIds : null,
        p_fecha_inicio: filters.start || null,
        p_fecha_fin: filters.end || null,
        p_busqueda: filters.search || null,
        p_limite: limit,
        p_offset: offset,
        p_entregable_ids: filters.deliverableIds?.length ? filters.deliverableIds : null,
        p_emisor: filters.emisor || null,
        } as never,
      );
      const page = toRows(unwrap(data, error) as unknown);
      collected.push(...page);
      if (page.length < limit) break;
    }
    return collected;
  }));
  const groups = await Promise.all(requests);
  const unique = new Map<string, JsonRecord>();
  for (const row of groups.flat()) unique.set(String(row.id), row);
  return [...unique.values()].filter((row) => {
    if (!filters.status) return true;
    const rawStatus = String(row.estatus ?? "").trim();
    const status = rawStatus.toLocaleLowerCase("es") === "atendido" ? "Cerrado" : rawStatus || "Sin estatus";
    return status === filters.status;
  }).sort((left, right) => {
    const byDate = String(right.fecha || "").localeCompare(String(left.fecha || ""));
    return byDate || String(left.folio || "").localeCompare(String(right.folio || ""));
  });
}

export type CommunicationStatistic = { id: string | null; etiqueta: string; total: number; porcentaje: number };
export type CommunicationPartyPreset = "Contratista" | "Residencia" | "Supervisión";
export type CommunicationPointStatus = "Abierto" | "En atención" | "Cerrado";
export type PointClassification = "Apertura de acuerdo" | "Informativo" | "Seguimiento" | "Cierre";
export type CommunicationRecipient = {
  id?: string;
  tipo_destinatario: CommunicationPartyPreset | "Otro";
  nombre_otro: string | null;
  fecha_recepcion: string | null;
  fecha_vencimiento?: string | null;
  atendido?: boolean;
  nombre?: string | null;
};
export type CommitmentDateHistory = { id: string; fecha_anterior: string; fecha_nueva: string; motivo: string; fecha_reprogramacion: string };
export type FollowUpNode = { antecedente_doc_id: string; antecedente_punto_id: string | null; folio: string; ordinal?: number; punto?: string | null };
export async function availableFollowUpNodes(context: WorkContext, fecha: string, areaId: string): Promise<FollowUpNode[]> {
  const { data, error } = await schema("control_documental").rpc("api_nodos_seguimiento_disponibles" as never, {
    p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id, p_fecha_limite: fecha, p_area_id: areaId || null,
  } as never);
  return unwrap(data, error) as unknown as FollowUpNode[];
}
export function communicationPointPayload({ clientId: _clientId, estatus: _estatus, ...point }: CommunicationPointDraft) {
  return { ...point, punto: point.punto.trim(), fecha_compromiso: point.requiere_seguimiento ? point.fecha_compromiso || null : null };
}
export type CommunicationStatus = "Abierto" | "En atención" | "Cerrado";
export type CommunicationPointDraft = {
  clasificacion: PointClassification;
  requiere_seguimiento: boolean;
  antecedente_doc_id: string | null;
  antecedente_punto_id: string | null;
  motivo_reprogramacion?: string;
  historial_fechas?: CommitmentDateHistory[];
  clientId: string;
  id?: string;
  punto: string;
  tipo_asunto_id: string;
  responsables: CommunicationPartyPreset[];
  fecha_compromiso: string;
  estatus: CommunicationPointStatus;
};
export type CommunicationPoint = {
  clasificacion: PointClassification;
  requiere_seguimiento: boolean;
  antecedente_doc_id: string | null;
  antecedente_punto_id: string | null;
  motivo_reprogramacion?: string;
  historial_fechas?: CommitmentDateHistory[];
  id: string;
  punto: string;
  tipo_asunto_id: string;
  tipo_asunto?: string | null;
  responsables: CommunicationPartyPreset[];
  fecha_compromiso: string | null;
  estatus: CommunicationPointStatus;
};
export type CommunicationFormValues = {
  destinatarios: CommunicationRecipient[];
  requiere_respuesta: boolean;
  dias_contestacion: number | null;
  titulo: string;
  autor_user_id: string | null;
  autor_nombre: string | null;
  participantes: string[];
  fecha: string;
  tipo_doc_id: string;
  folio: string;
  resumen: string;
  emisor: string;
  destinatario: string | null;
  frente_id: string;
  area_id: string;
  actividad_material_id: string[];
  entregable_id: string[];
  subfrente_id: string[];
  estimaciones_id: string[];
  puntos: CommunicationPointDraft[];
};
export type CommunicationDetail = {
  destinatarios: CommunicationRecipient[];
  requiere_respuesta: boolean;
  dias_contestacion: number | null;
  titulo: string | null;
  autor_user_id: string | null;
  autor_nombre: string | null;
  participantes: string[];
  id: string;
  fecha: string;
  tipo_doc_id: string;
  actividad_material_id: string[];
  entregable_id?: string[];
  subfrente_id: string[];
  folio: string;
  resumen: string;
  emisor: string | null;
  destinatario: string | null;
  estatus: CommunicationStatus | null;
  proyecto_id: string;
  contrato_id: string;
  frente_id: string | null;
  tipo_asunto_id: string[];
  estimaciones_id: string[];
  area_id: string | null;
  puntos: CommunicationPoint[];
};
export type CommunicationDashboardStatus = "Abierto" | "En atención" | "Cerrado";
export type CommunicationDashboard = {
  total: number;
  totalFiltrado: number;
  porTipo: CommunicationStatistic[];
  porEstatus: Array<{ etiqueta: CommunicationDashboardStatus; total: number; porcentaje: number }>;
  porFrente: CommunicationStatistic[];
  porFrenteTipo: Array<{ frenteId: string | null; frente: string; tipoId: string | null; tipo: string; total: number; porcentaje: number }>;
  porFrenteEstatus: Array<{ id: string | null; etiqueta: string; estatus: CommunicationDashboardStatus; total: number; porcentaje: number }>;
  porSubfrente: CommunicationStatistic[];
  porSubfrenteTipo: Array<{ id: string | null; etiqueta: string; tipoId: string | null; tipo: string; total: number; porcentaje: number }>;
  porSubfrenteEstatus: Array<{ id: string | null; etiqueta: string; estatus: CommunicationDashboardStatus; total: number; porcentaje: number }>;
  kpis: {
    totalComunicaciones: number;
    comunicacionesAbiertas: { total: number; porcentaje: number };
    abiertasSinSeguimiento: { total: number; porcentaje: number };
    focoAtencion: null | { nivel: "frente" | "subfrente"; id: string | null; etiqueta: string; total: number };
  };
  frenteSeleccionado: { id: string; etiqueta: string } | null;
  filtros: { fechaInicio: string | null; fechaFin: string | null; tipoDocumentoId: string | null; frenteId: string | null; frenteIds: string[] | null };
};


export type CommitmentDueStatus = "Vencido" | "Vence hoy" | "Próximo a vencer" | "En plazo" | "Sin fecha" | "Cerrado";

export type CommunicationCommitment = {
  puntoId: string | null;
  destinatarioId?: string | null;
  destinatario?: string | null;
  tipoCompromiso?: "punto_minuta" | "respuesta_comunicacion";
  comunicacionId: string;
  folio: string | null;
  titulo: string | null;
  resumen: string | null;
  punto: string | null;
  fechaComunicacion: string | null;
  fechaCompromiso: string | null;
  diasParaVencer: number | null;
  estadoVencimiento: CommitmentDueStatus;
  estatus: CommunicationPointStatus;
  responsables: string[];
  tipoAsuntoId: string | null;
  tipoAsunto: string | null;
  areaId: string | null;
  area: string | null;
  frenteId: string | null;
  frente: string | null;
  subfrentes: Array<{ id: string; nombre: string }>;
  tieneSeguimiento: boolean;
  totalSeguimientos: number;
};

export type CommitmentDashboard = {
  fechaCorte: string;
  kpis: {
    totalCompromisos: number;
    abiertos: number;
    cerrados: number;
    vencidos: number;
    venceHoy: number;
    proximos3Dias: number;
    sinFecha: number;
    porcentajeVencidos: number;
    promedioDiasAtraso: number;
  };
  porEstado: Array<{ estado: CommitmentDueStatus; total: number }>;
  porArea: Array<{ areaId: string | null; area: string; total: number; abiertos: number; vencidos: number }>;
  porFrente: Array<{ frenteId: string | null; frente: string; total: number; abiertos: number; vencidos: number }>;
  porSemana: Array<{ semanaInicio: string; total: number }>;
  focoAtencion: { frenteId: string | null; frente: string; vencidos: number } | null;
};

export type CommitmentAlertType = "vence_3_dias" | "vence_1_dia" | "vence_hoy" | "vencido";

export type CommitmentAlert = {
  id: string;
  tipoAlerta: CommitmentAlertType;
  fechaAlerta: string;
  fechaCompromiso: string;
  diasParaVencer: number;
  titulo: string;
  mensaje: string;
  leidaAt: string | null;
  createdAt: string;
  comunicacionId: string;
  folio: string | null;
  tituloComunicacion: string | null;
  puntoId: string | null;
  destinatarioId?: string | null;
  destinatario?: string | null;
  tipoCompromiso?: "punto_minuta" | "respuesta_comunicacion";
  punto: string | null;
  areaId: string | null;
  area: string | null;
  frenteId: string | null;
  frente: string | null;
};

export type CommitmentAlertsResponse = {
  noLeidas: number;
  items: CommitmentAlert[];
};

export type CommitmentCalendarFilters = {
  start: string;
  end: string;
  areaId?: string;
  frontId?: string;
  subfrontId?: string;
  dueStatus?: CommitmentDueStatus | "";
};

export async function communicationCommitmentCalendar(context: WorkContext, filters: CommitmentCalendarFilters) {
  const { data, error } = await schema("control_documental").rpc(
    "api_calendario_compromisos" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_fecha_inicio: filters.start || null,
      p_fecha_fin: filters.end || null,
      p_area_id: filters.areaId || null,
      p_frente_id: filters.frontId || null,
      p_subfrente_id: filters.subfrontId || null,
      p_estado_vencimiento: filters.dueStatus || null,
    } as never,
  );
  return unwrap(data, error) as unknown as CommunicationCommitment[];
}

export async function communicationCommitmentDashboard(context: WorkContext, areaId?: string) {
  const { data, error } = await schema("control_documental").rpc(
    "api_dashboard_compromisos" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_area_id: areaId || null,
    } as never,
  );
  return unwrap(data, error) as unknown as CommitmentDashboard;
}

export async function communicationCommitmentAlerts(
  context: WorkContext,
  options: { onlyUnread?: boolean; limit?: number; offset?: number } = {},
) {
  const { data, error } = await schema("control_documental").rpc(
    "api_alertas_compromisos" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_solo_no_leidas: options.onlyUnread === true,
      p_limite: options.limit ?? 50,
      p_offset: options.offset ?? 0,
    } as never,
  );
  return unwrap(data, error) as unknown as CommitmentAlertsResponse;
}

export async function markCommunicationCommitmentAlertRead(id: string) {
  const { data, error } = await schema("control_documental").rpc(
    "api_marcar_alerta_compromiso_leida" as never,
    { p_alerta_id: id } as never,
  );
  return unwrap(data, error) as unknown;
}

export async function markAllCommunicationCommitmentAlertsRead(context: WorkContext) {
  const { data, error } = await schema("control_documental").rpc(
    "api_marcar_alertas_compromisos_leidas" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
    } as never,
  );
  return unwrap(data, error) as unknown as number;
}

export async function communicationStatistics(context: WorkContext, filters: { start?: string; end?: string; typeId?: string; frontIds?: string[] }) {
  const frontIds = filters.frontIds ?? [];
  const { data, error } = await schema("control_documental").rpc(
    "api_estadisticas_comunicaciones" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_fecha_inicio: filters.start || null,
      p_fecha_fin: filters.end || null,
      p_tipo_doc_id: filters.typeId || null,
      p_frente_id: frontIds.length === 1 ? frontIds[0] : null,
      p_frente_ids: frontIds.length ? frontIds : null,
    } as never,
  );
  return unwrap(data, error) as unknown as CommunicationDashboard;
}

export type BulkCommunicationRecord = { fecha: string; folio: string; resumen: string };

export async function bulkCreateCommunications(context: WorkContext, areaId: string, records: BulkCommunicationRecord[]) {
  const { data, error } = await schema("control_documental").rpc(
    "api_carga_masiva_comunicaciones" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_area_id: areaId,
      p_registros: records,
    } as never,
  );
  return unwrap(data, error) as unknown as { insertados: number; registros?: JsonRecord[] };
}

export type CommunicationAreaPerson = { user_id: string; nombre: string };

export async function listCommunicationAreaPersonnel(context: WorkContext, areaId: string): Promise<CommunicationAreaPerson[]> {
  const { data, error } = await schema("control_documental").rpc(
    "api_personal_area_comunicacion" as never,
    { p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id, p_area_id: areaId } as never,
  );
  const response = unwrap(data, error) as unknown;
  if (!Array.isArray(response)) throw new Error("La RPC de personal no devolvió un listado válido.");

  return response
    .map((person) => {
      const row = person as { userId?: unknown; user_id?: unknown; nombre?: unknown; nombre_completo?: unknown };
      const userId = String(row.userId ?? row.user_id ?? "").trim();
      const nombre = String(row.nombre ?? row.nombre_completo ?? "").trim() || "Sin nombre";
      return { user_id: userId, nombre };
    })
    .filter((person) => Boolean(person.user_id));
}

export async function createCommunication(
  context: WorkContext,
  values: CommunicationFormValues,
  file: File,
  followUp?: { parentId: string; typeId?: string; pointId?: string | null; targetPointOrdinal?: number | null },
) {
  if (followUp && !followUp.typeId) throw new Error("El tipo de seguimiento es obligatorio.");
  const metadata = {
    proyectoId: context.proyecto_id,
    contratoId: context.contrato_id,
    areaId: values.area_id || context.area_id,
    fecha: values.fecha,
    tipoDocId: values.tipo_doc_id,
    actividadMaterialIds: values.actividad_material_id || [],
    entregableIds: values.entregable_id || [],
    subfrenteIds: values.subfrente_id || [],
    folio: values.folio,
    titulo: values.titulo.trim(),
    autorUserId: values.autor_user_id,
    participantes: values.participantes,
    resumen: values.resumen,
    emisor: values.emisor.trim(),
    frenteId: values.frente_id || null,
    destinatarios: values.destinatarios,
    requiereRespuesta: values.requiere_respuesta,
    diasContestacion: values.dias_contestacion,
    puntos: values.puntos.map(communicationPointPayload),
    archivoNombre: file.name,
    docPadreId: followUp?.parentId || null,
    tipoSeguimientoId: followUp?.typeId || null,
    puntoDocId: followUp?.pointId || null,
    puntoHijoOrdinal: followUp?.targetPointOrdinal ?? null,
  };
  const body = new FormData();
  body.append("metadata", JSON.stringify(metadata));
  body.append("file", file, file.name);
  const { data, error } = await supabase.functions.invoke("comunicacion-oficial", { body });
  if (error) return throwFunctionError(error);
  return data as JsonRecord;
}

export async function getCommunication(id: string) {
  const { data, error } = await schema("control_documental").rpc(
    "api_obtener_comunicacion_oficial_v2" as never,
    { p_comunicacion_id: id } as never,
  );
  return unwrap(data, error) as unknown as CommunicationDetail;
}

export async function updateCommunication(id: string, values: CommunicationFormValues) {
  const { data, error } = await schema("control_documental").rpc(
    "api_actualizar_comunicacion_oficial_v5" as never,
    {
      p_comunicacion_id: id,
      p_area_id: values.area_id || null,
      p_fecha: values.fecha,
      p_tipo_doc_id: values.tipo_doc_id,
      p_actividad_material_id: values.actividad_material_id || [],
      p_entregable_id: values.entregable_id || [],
      p_subfrente_id: values.subfrente_id || [],
      p_folio: values.folio,
      p_titulo: values.titulo.trim(),
      p_autor_user_id: values.autor_user_id,
      p_participantes: values.participantes,
      p_resumen: values.resumen,
      p_emisor: values.emisor.trim(),
      p_frente_id: values.frente_id || null,
      p_estimaciones_id: values.estimaciones_id || [],
      p_destinatarios: values.destinatarios,
      p_requiere_respuesta: values.requiere_respuesta,
      p_dias_contestacion: values.dias_contestacion,
      p_puntos: values.puntos.map(communicationPointPayload),
    } as never,
  );
  return unwrap(data, error) as unknown;
}

export async function replaceCommunicationPdf(id: string, file: File) {
  const metadata = {
    action: "replace",
    comunicacionId: id,
    archivoNombre: file.name,
  };
  const body = new FormData();
  body.append("metadata", JSON.stringify(metadata));
  body.append("file", file, file.name);
  const { data, error } = await supabase.functions.invoke("comunicacion-oficial", { body });
  if (error) return throwFunctionError(error);
  return data as JsonRecord;
}

async function invokeCommunicationFunction(body: JsonRecord) {
  const { data, error } = await supabase.functions.invoke("comunicacion-oficial", { body });
  if (error) return throwFunctionError(error);
  return data as JsonRecord;
}

export async function deleteCommunication(id: string) {
  return invokeCommunicationFunction({ action: "delete", comunicacionId: id });
}

export async function getCommunicationPdf(id: string) {
  return invokeCommunicationFunction({ action: "signed-url", comunicacionId: id });
}

export type CommunicationFollowUpValues = {
  area_id: string | null;
  doc_padre_id: string;
  doc_hijo_id: string | null;
  punto_doc_id: string | null;
  punto_hijo_id: string | null;
  destinatario_id?: string | null;
  clase?: "Seguimiento" | "Respuesta" | "Cierre";
  tipo_id: string;
  fecha: string | null;
  resumen: string | null;
};

export type CommunicationFollowUp = JsonRecord & {
  id?: string;
  doc_padre_id: string;
  doc_hijo_id: string | null;
  alcance: "general" | "punto";
  punto_doc_id: string | null;
  punto?: string | null;
  punto_hijo_id?: string | null;
  punto_hijo?: string | null;
  punto_hijo_estatus?: CommunicationPointStatus | null;
  punto_hijo_fecha_compromiso?: string | null;
  punto_hijo_responsables?: CommunicationPartyPreset[] | null;
  alcance_destino?: "comunicacion" | "punto" | "otro_medio";
  punto_estatus?: CommunicationPointStatus | null;
  punto_fecha_compromiso?: string | null;
  punto_responsables?: CommunicationPartyPreset[] | null;
  modalidad?: "comunicacion" | "otro_medio";
  tipo?: string | null;
  folio?: string | null;
  folio_padre?: string | null;
  fecha?: string | null;
  fecha_padre?: string | null;
  fecha_hijo?: string | null;
  resumen?: string | null;
  deleted_at?: string | null;
};

export async function createCommunicationFollowUp(values: CommunicationFollowUpValues) {
  if (!values.tipo_id) throw new Error("El tipo de seguimiento es obligatorio.");
  if (!values.area_id) throw new Error("El área es obligatoria.");
  if (!values.doc_padre_id) throw new Error("La comunicación origen es obligatoria.");
  if (values.doc_hijo_id === null) {
    if (!values.fecha) throw new Error("La fecha es obligatoria.");
    if (!values.resumen?.trim()) throw new Error("El resumen es obligatorio.");
  } else if (!values.doc_hijo_id) {
    throw new Error("Seleccione la comunicación que desea relacionar.");
  }
  if (values.doc_hijo_id) {
    const { data, error } = await schema("control_documental").rpc("api_crear_seguimiento_documental_v3" as never, {
      p_doc_padre_id: values.doc_padre_id, p_punto_doc_id: values.punto_doc_id,
      p_destinatario_id: values.destinatario_id || null, p_doc_hijo_id: values.doc_hijo_id,
      p_punto_hijo_id: values.punto_hijo_id, p_clase: values.clase || "Seguimiento", p_resumen: values.resumen,
    } as never);
    return unwrap(data, error) as unknown;
  }
  const { data, error } = await schema("control_documental").rpc(
    "api_crear_seguimiento_documental_v2" as never,
    {
      p_area_id: values.area_id,
      p_doc_padre_id: values.doc_padre_id,
      p_doc_hijo_id: values.doc_hijo_id,
      p_punto_doc_id: values.punto_doc_id,
      p_punto_hijo_id: values.punto_hijo_id,
      p_tipo_id: values.tipo_id,
      p_fecha: values.fecha,
      p_resumen: values.resumen,
    } as never,
  );
  return unwrap(data, error) as unknown;
}

export async function listCommunicationFollowUps(context: WorkContext) {
  const { data, error } = await schema("control_documental").rpc(
    "api_listar_seguimientos_documentales" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_area_id: null,
    } as never,
  );
  return unwrap(data, error) as unknown as CommunicationFollowUp[];
}

export async function relationComEst(
  context: WorkContext,
  filters: { frontId?: string; onlyAfterEstimation?: boolean } = {},
) {
  const { data, error } = await schema("control_documental").rpc(
    "api_relacion_comunicacion_estimacion" as never,
    {
      p_proyecto_id: context.proyecto_id,
      p_contrato_id: context.contrato_id,
      p_frente_id: filters.frontId || null,
      p_solo_posteriores: filters.onlyAfterEstimation === true,
    } as never,
  );
  return toRows(unwrap(data, error) as unknown);
}

export async function boltCatalogs(context: WorkContext) {
  const { data, error } = await schema("calidad").rpc(
    "api_catalogos_reportes_conexiones_atornilladas" as never,
    { p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id } as never,
  );
  return unwrap(data, error) as unknown as JsonRecord;
}

export async function listBoltReports(context: WorkContext) {
  const { data, error } = await schema("calidad").rpc(
    "api_listar_reportes_conexiones_atornilladas" as never,
    { p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id, p_filtros: {} } as never,
  );
  return unwrap(data, error) as unknown;
}

export async function saveBoltReport(context: WorkContext, values: JsonRecord, id?: string) {
  const rpc = id ? "api_editar_reporte_conexion_atornillada" : "api_insertar_reporte_conexion_atornillada";
  const args = id
    ? { p_reporte_id: id, p_datos: values }
    : { p_proyecto_id: context.proyecto_id, p_contrato_id: context.contrato_id, p_datos: values };
  const { data, error } = await schema("calidad").rpc(rpc as never, args as never);
  return unwrap(data, error) as unknown;
}

export async function deleteBoltReport(id: string) {
  const { data, error } = await schema("calidad").rpc(
    "api_eliminar_reporte_conexion_atornillada" as never,
    { p_reporte_id: id } as never,
  );
  return unwrap(data, error) as unknown;
}

export function toRows(value: unknown): JsonRecord[] {
  if (Array.isArray(value)) return value as JsonRecord[];
  if (!value || typeof value !== "object") return [];
  const object = value as JsonRecord;
  for (const key of ["data", "items", "registros", "resultados", "comunicaciones", "rows"]) {
    if (Array.isArray(object[key])) return object[key] as JsonRecord[];
  }
  return [];
}
